import { useId } from 'react';
import { cn } from '@/lib/utils';
import { useWebsiteT } from '../hooks/use-website-t';

export interface EditModeSwitchProps {
  readonly enabled: boolean;
  readonly onChange: (enabled: boolean) => void;
  readonly disabled?: boolean;
}

/**
 * The Edit mode switch. Dukanti lime when on. It only shows or hides the
 * overlay of the canvas: it never reloads the frame or changes its route.
 */
export function EditModeSwitch({
  enabled,
  onChange,
  disabled,
}: EditModeSwitchProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const labelId = useId();
  return (
    <div className="flex items-center gap-2">
      <span id={labelId} className="text-foreground text-sm font-medium">
        {t('admin.websiteTemplates.editor.editMode')}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-labelledby={labelId}
        disabled={disabled}
        data-testid="edit-mode-switch"
        onClick={() => onChange(!enabled)}
        className={cn(
          'focus-visible:ring-ring/50 relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-transparent outline-none transition-colors focus-visible:ring disabled:cursor-not-allowed disabled:opacity-50',
          enabled ? 'bg-admin-lime' : 'bg-muted',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'bg-card shadow-admin-card pointer-events-none block size-5 rounded-full transition-all',
            enabled ? 'ms-[1.375rem]' : 'ms-0.5',
          )}
        />
      </button>
      <span
        aria-hidden="true"
        className="text-muted-foreground w-7 text-xs font-semibold uppercase"
      >
        {enabled ? t('admin.websiteTemplates.editor.on') : t('admin.websiteTemplates.editor.off')}
      </span>
    </div>
  );
}
